import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SiteSettingsContext = createContext({
  settings: null,
  loading: true,
  refresh: () => Promise.resolve(),
});

/**
 * SiteSettingsProvider — fetches `/api/site-settings` on mount and exposes
 * the result via context. Used to gate the storefront in maintenance mode
 * and to hide the PayPal button when online payments are disabled.
 *
 * Re-polls every 60 seconds so an admin flipping the toggle is reflected
 * for already-loaded customer tabs within a minute.
 */
export const SiteSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/site-settings`, { cache: 'no-store' });
      if (r.ok) {
        setSettings(await r.json());
      }
    } catch (_) {
      // Silent — fallback to last known good settings so a network blip
      // can't accidentally lock the customer out.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, 60_000);
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [refresh]);

  return (
    <SiteSettingsContext.Provider value={{ settings, loading, refresh }}>
      {children}
    </SiteSettingsContext.Provider>
  );
};

export const useSiteSettings = () => useContext(SiteSettingsContext);
